import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledResearchSection = styled.section`
  max-width: 900px;

  .scholar-link {
    ${({ theme }) => theme.mixins.inlineLink};
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }
`;

const StyledPaperList = styled.ol`
  ${({ theme }) => theme.mixins.resetList};
  margin: 0 0 30px;
  counter-reset: paper;
`;

const StyledPaper = styled.li`
  position: relative;
  padding: 25px 25px 25px 30px;
  border-left: 2px solid var(--lightest-navy);
  transition: var(--transition);

  &:hover,
  &:focus-within {
    border-left-color: var(--green);
    background-color: var(--light-navy);
  }

  @media (max-width: 480px) {
    padding: 20px 0 20px 20px;
  }

  .paper-title {
    margin: 0 0 8px;
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
    font-weight: 600;
    line-height: 1.3;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      color: var(--lightest-slate);
    }
  }

  .paper-authors {
    margin: 0 0 4px;
    color: var(--slate);
    font-size: var(--fz-md);
  }

  .paper-venue {
    margin: 0 0 12px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    line-height: 1.6;
  }

  .paper-summary {
    margin: 0 0 14px;
    max-width: 68ch;
    color: var(--slate);
    font-size: var(--fz-md);

    p {
      margin: 0;
    }
  }

  .paper-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 18px;
  }

  .paper-award {
    display: inline-block;
    padding: 4px 10px;
    border: 1px solid var(--green);
    border-radius: var(--border-radius);
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    transition: var(--transition);

    &:hover,
    &:focus {
      background-color: var(--green-tint);
    }
  }

  .paper-doi {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    word-break: break-word;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }
`;

const Research = () => {
  const data = useStaticQuery(graphql`
    query {
      research: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/research/" } }
        sort: { fields: [frontmatter___order], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              order
              title
              authors
              venue
              place
              published
              pages
              doi
              url
              award
              awardUrl
            }
            html
          }
        }
      }
    }
  `);

  const papers = data.research.edges;
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledResearchSection id="research" ref={revealContainer}>
      <h2 className="numbered-heading">Research &amp; Publications</h2>

      <StyledPaperList>
        {papers &&
          papers.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { title, authors, venue, place, published, pages, doi, url, award, awardUrl } =
              frontmatter;

            const venueLine = [venue, place, published, pages].filter(Boolean).join(', ');

            return (
              <StyledPaper key={i}>
                <h3 className="paper-title">
                  {url ? (
                    <a href={url} target="_blank" rel="noreferrer">
                      {title}
                    </a>
                  ) : (
                    title
                  )}
                </h3>

                <p className="paper-authors">{authors}</p>
                <p className="paper-venue">{venueLine}</p>

                <div className="paper-summary" dangerouslySetInnerHTML={{ __html: html }} />

                <div className="paper-meta">
                  {award &&
                    (awardUrl ? (
                      <a
                        className="paper-award"
                        href={awardUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${award} certificate for ${title}`}>
                        {award}
                      </a>
                    ) : (
                      <span className="paper-award">{award}</span>
                    ))}

                  {doi && (
                    <span className="paper-doi">
                      doi{' '}
                      <a href={`https://doi.org/${doi}`} target="_blank" rel="noreferrer">
                        {doi}
                      </a>
                    </span>
                  )}
                </div>
              </StyledPaper>
            );
          })}
      </StyledPaperList>

      <a
        className="scholar-link"
        href="https://scholar.google.com/citations?user=GqqPsasAAAAJ"
        target="_blank"
        rel="noreferrer">
        View on Google Scholar
      </a>
    </StyledResearchSection>
  );
};

export default Research;
